export type SupplierTier = 'FREE' | 'BASIC' | 'ENTERPRISE';

export type NavItem = {
    label: string;
    to: string;
    icon?: string;
    section?: string;
    comingSoon?: boolean;
    requiredTier?: SupplierTier;
    /**
     * Gated feature this item belongs to. The item is hidden entirely unless the
     * user's `features[feature]` is true (AppLayout filters on it, and the router
     * guard in router/index.ts refuses the route). Use it for things not yet
     * released to everyone — unlike `requiredTier`, which shows a locked item.
     */
    feature?: string;
    /** `data-tour` key for the guided tour (see src/onboarding/tours.ts). */
    tour?: string;
    /** One-line hover tooltip clarifying what the item is (shown even when the sidebar is expanded). */
    hint?: string;
    /** Shorter label for tight spots (the Companion's phone tab bar). */
    shortLabel?: string;
};

export const supplierNavItems: NavItem[] = [
    { label: 'Dashboard', to: '/supplier', icon: 'LayoutDashboard', section: 'Main', requiredTier: 'BASIC' },
    { label: 'Company Profile', to: '/supplier/profile', icon: 'Building2', section: 'Main', tour: 'nav-profile' },
    { label: 'Products', to: '/supplier/products', icon: 'Package', section: 'Main', tour: 'nav-products' },
    { label: 'Promotions', to: '/supplier/promotions', icon: 'Target', section: 'Main', tour: 'nav-promotions' },
    { label: 'Quotes', to: '/supplier/quotes', icon: 'MessageSquare', section: 'Main', requiredTier: 'ENTERPRISE' },
    // MDF is open to FREE suppliers as a 30-day trial (banner in the views;
    // trial mechanics come later).
    { label: 'MDF Requests', to: '/supplier/mdf/requests', icon: 'DollarSign', section: 'Main', tour: 'nav-mdf-requests' },
    { label: 'MDF Offerings', to: '/supplier/mdf/offerings', icon: 'Megaphone', section: 'Main' },
    { label: 'Competitors', to: '/supplier/competitive-landscape', icon: 'BarChart3', section: 'Main', requiredTier: 'ENTERPRISE' },
    { label: 'Channel Team', to: '/supplier/contacts', icon: 'Users' },
    { label: 'Content Library', to: '/supplier/training', icon: 'BookOpen', requiredTier: 'ENTERPRISE' },
    { label: 'Metrics Library', to: '/supplier/metrics', icon: 'TrendingUp', section: 'Main', requiredTier: 'ENTERPRISE' },
    { label: 'Solution Map', to: '/supplier/solution-map', icon: 'Map', section: 'Main', requiredTier: 'ENTERPRISE' },
    { label: 'Team Members', to: '/supplier/team', icon: 'UserCog', section: 'Settings' },
    { label: 'Settings', to: '/supplier/settings', icon: 'Settings', section: 'Settings', requiredTier: 'BASIC' },
    { label: 'Connect AI', to: '/connect-ai', icon: 'Plug', section: 'Settings', requiredTier: 'BASIC' },
    { label: 'Documentation', to: '/resources/docs', icon: 'FileText', section: 'Resources' },
    { label: 'Support', to: '/resources/support', icon: 'LifeBuoy', section: 'Resources', tour: 'nav-support' },
];

/**
 * FUTURE NAV ITEMS (not built yet):
 *
 * Deal Management:
 * - Forecasting: Pipeline forecast by close date, MRC projections
 * - Commission Tracker: Track expected vs received commissions across TSDs
 * - Deal Templates: Save common deal patterns for quick re-use
 *
 * Account Management:
 * - Account Health: At-a-glance health scores across all accounts
 * - Renewal Tracker: Contracts expiring soon across all accounts
 * - Account Activity Feed: Chronological feed of all activities across accounts
 *
 * Team (future multi-user):
 * - Team Pipeline: View across all team members' deals
 * - Team Leaderboard: Performance metrics
 * - Shared Deal Templates
 *
 * Integrations:
 * - CRM Sync: HubSpot/Zoho/Salesforce bi-directional sync
 * - Calendar: Meeting scheduling integration
 * - Email: Forward emails to auto-create activities
 */
export const advisorNavItems: NavItem[] = [
    { label: 'Today', to: '/advisor/today', icon: 'LayoutDashboard', tour: 'nav-today' },
    { label: 'Pipeline', to: '/advisor/pipeline', icon: 'Kanban' },
    { label: 'Accounts', to: '/advisor/workspace/accounts', icon: 'Building', tour: 'nav-accounts' },
    { label: 'Supplier Directory', to: '/advisor/suppliers', icon: 'Building2', tour: 'nav-suppliers' },
    { label: 'MDF Requests', to: '/advisor/mdf/requests', icon: 'DollarSign' },
    { label: 'MDF Marketplace', to: '/advisor/mdf/marketplace', icon: 'Megaphone' },
    { label: 'Community', to: '/advisor/community', icon: 'MessageCircle', comingSoon: true },
    { label: 'Contacts', to: '/advisor/contacts', icon: 'Users' },
    { label: 'Broadband Lookup', to: '/advisor/broadband', icon: 'Wifi', hint: 'Raw FCC broadband data for an address (census block + providers on file)' },
    { label: 'Sweep', to: '/advisor/sweep', icon: 'Radar', feature: 'sweep', hint: 'Find carriers at one or many sites, request quotes, and build a client proposal' },
    { label: 'Metrics', to: '/advisor/metrics', icon: 'TrendingUp', comingSoon: true },
    { label: 'Connect AI', to: '/connect-ai', icon: 'Plug', section: 'Settings' },
];

export const cmNavItems: NavItem[] = [
    { label: 'Morning Briefing', shortLabel: 'Briefing', to: '/cm/today', icon: 'Sunrise', section: 'Main' },
    { label: 'Your book', shortLabel: 'Book', to: '/cm/book', icon: 'BookOpen', section: 'Main' },
    { label: 'Something to bring', shortLabel: 'Bring', to: '/cm/bring', icon: 'Lightbulb', section: 'Main' },
    { label: 'Your way', to: '/cm/yourway', icon: 'SlidersHorizontal', section: 'Main' },
    // A door, not a workspace: CompanionLayout draws a divider above it.
    { label: 'Inside Claude', shortLabel: 'Claude', to: '/cm/claude', icon: 'Plug', section: 'Doors' },
];

export const adminNavItems: NavItem[] = [
    { label: 'Dashboard', to: '/admin', icon: 'LayoutDashboard', section: 'Main' },
    { label: 'AI Models', to: '/admin/ai-models', icon: 'Brain', section: 'Main' },
    { label: 'AI Prompts', to: '/admin/ai-prompts', icon: 'Lightbulb', section: 'Main' },
    { label: 'AI Diagnostics', to: '/admin/ai-diagnostics', icon: 'Microscope', section: 'Main' },
    { label: 'AI Generation Log', to: '/admin/ai-generations', icon: 'Sparkles', section: 'Main' },
    { label: 'Analytics', to: '/admin/analytics', icon: 'BarChart3', section: 'Main' },
    { label: 'Activity Log', to: '/admin/activity', icon: 'Activity', section: 'Main' },
    { label: 'Quote Queue', to: '/admin/quotes', icon: 'FileText', section: 'Main' },
    { label: 'MDF Activity', to: '/admin/mdf', icon: 'DollarSign', section: 'Main' },
    { label: 'Suppliers', to: '/admin/suppliers', icon: 'Building2', section: 'Management' },
    { label: 'Taxonomy', to: '/admin/taxonomy', icon: 'Tag', section: 'Main' },
    { label: 'Landscape Relevance', to: '/admin/landscape-relevance', icon: 'SlidersHorizontal', section: 'Main' },
    { label: 'TSDs', to: '/admin/tsds', icon: 'Handshake', section: 'Management' },
    { label: 'TSD Contacts', to: '/admin/tsd-contacts', icon: 'ContactRound', section: 'Management' },
    { label: 'Users', to: '/admin/users', icon: 'Users', section: 'Management' },
    { label: 'Channel Managers', to: '/admin/channel-managers', icon: 'UserCog', section: 'Management' },
    { label: 'Sweep Suppliers', to: '/admin/sweep-suppliers', icon: 'Radar', section: 'Management' },
    { label: 'Invite Codes', to: '/admin/invite-codes', icon: 'Ticket', section: 'Management' },
    { label: 'Create User', to: '/admin/create-user', icon: 'UserPlus', section: 'Management' },
    { label: 'Legal Documents', to: '/admin/legal', icon: 'Landmark', section: 'Management' },
];
